import React, { useEffect, useState, useRef } from 'react';
import { Spin, Typography } from 'antd';

const { Text } = Typography;

/**
 * MapLoadingOverlay - Premium UX loading with real-time hazard detection
 * Option B: Minimum 3s display + REAL-TIME hazard count detection + 10s timeout + smooth fade
 * ⚡ REAL-TIME: No polling delays - responds instantly to hazard count changes
 */
const MapLoadingOverlay = ({ 
  visible = false,
  hazardCount = 0
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [minimumTimePassed, setMinimumTimePassed] = useState(false);
  
  // Refs for cleanup
  const minimumTimeoutRef = useRef(null);
  const maxTimeoutRef = useRef(null);
  const fadeTimeoutRef = useRef(null);
  
  // Cleanup function
  const cleanup = () => {
    if (minimumTimeoutRef.current) clearTimeout(minimumTimeoutRef.current);
    if (maxTimeoutRef.current) clearTimeout(maxTimeoutRef.current);
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
  };

  // Smooth fade-out function
  const fadeOut = () => {
    console.log('🎨 MapLoadingOverlay: Starting fade-out animation');
    setOpacity(0);
    
    fadeTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setShowOverlay(false);
      console.log('🎨 MapLoadingOverlay: Fade-out complete, overlay hidden');
    }, 500); // 0.5 second fade
  };

  // Initialize loading when visible changes
  useEffect(() => {
    if (visible) {
      console.log('🎯 MapLoadingOverlay: Starting premium loading sequence');
      setShowOverlay(true);
      setIsVisible(true);
      setOpacity(1);
      setMinimumTimePassed(false);
      
      // PHASE 1: Minimum 3-second display (prevents flicker)
      minimumTimeoutRef.current = setTimeout(() => {
        console.log('🎯 MapLoadingOverlay: Minimum 3s elapsed, enabling real-time hazard detection');
        setMinimumTimePassed(true);
      }, 3000);
      
      // PHASE 3: Maximum 10-second timeout (safety net)
      maxTimeoutRef.current = setTimeout(() => {
        console.log('⏰ MapLoadingOverlay: 10s timeout reached, forcing fade-out');
        cleanup();
        fadeOut();
      }, 10000);

      return cleanup;
    } else {
      cleanup();
      setShowOverlay(false);
      setIsVisible(false);
      setOpacity(1);
      setMinimumTimePassed(false);
    }
  }, [visible]);

  // ⚡ REAL-TIME HAZARD DETECTION: Responds instantly to hazard count changes
  useEffect(() => {
    if (visible && minimumTimePassed && hazardCount > 0) {
      console.log(`✅ MapLoadingOverlay: REAL-TIME hazard detection! ${hazardCount} hazards found - triggering immediate fade-out`);
      cleanup();
      fadeOut();
    }
  }, [visible, minimumTimePassed, hazardCount]);

  if (!showOverlay || !isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '24px 32px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        border: '1px solid #f0f0f0',
        opacity: opacity,
        transition: 'opacity 0.5s ease-out'
      }}
    >
      <Spin size="large" />
      <Text style={{ fontSize: '16px', color: '#39325b', fontWeight: 500 }}>
        Loading...
      </Text>
    </div>
  );
};

export default MapLoadingOverlay; 
 